/**
 * The last line against a blank screen.
 *
 * React unmounts the entire tree when a render throws, which leaves the operator looking at nothing at all —
 * the one failure this product may not have, because a blank screen is indistinguishable from a screen that
 * loaded and had nothing on it. This catches that and says what happened, in the terms that matter here:
 * the fault is in showing the record, not in the record, and nothing has been written.
 */
import { Component, type ErrorInfo, type ReactNode } from "react";
import { Button } from "./ui/index.js";

interface Props {
  children: ReactNode;
}
interface State {
  message: string | null;
}

export class ErrorBoundary extends Component<Props, State> {
  override state: State = { message: null };

  static getDerivedStateFromError(error: unknown): State {
    return { message: error instanceof Error ? error.message : String(error) };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // eslint-disable-next-line no-console -- the browser console is the only place this can be left
    console.error("Corpex EBR: a screen failed to render", error, info.componentStack);
  }

  override render(): ReactNode {
    if (this.state.message === null) return this.props.children;
    return (
      <main role="alert" className="flex max-w-[640px] flex-col items-start gap-3 bg-page p-6">
        <h1 className="m-0 font-display text-2xl font-semibold text-ink">This screen could not be shown</h1>
        <p className="m-0 text-[15px] leading-6 text-ink-muted">
          Something went wrong while drawing this page. The fault is in showing the record, not in the record
          itself: nothing has been written, changed or lost. If it happens again, quote this to whoever
          supports the system.
        </p>
        <code className="break-words rounded-md bg-subtle px-3 py-2 font-mono text-[13px] text-ink">
          {this.state.message}
        </code>
        <Button variant="primary" onClick={() => this.setState({ message: null })}>
          Try again
        </Button>
      </main>
    );
  }
}
